import { CartAddEvent, CartErrorEvent } from '@theme/events';

if (!customElements.get('cro-accessory-rail')) {
  customElements.define(
    'cro-accessory-rail',
    class extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('.cro-acc__grid');
        this.prev = this.querySelector('.cro-acc__arrow--prev');
        this.next = this.querySelector('.cro-acc__arrow--next');
        this.prev?.addEventListener('click', () => this.slide(-1));
        this.next?.addEventListener('click', () => this.slide(1));
        this.resizeObserver = new ResizeObserver(() => this.syncArrows());
        if (this.rail) this.resizeObserver.observe(this.rail);

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('change', this.onChange);
        this.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      syncArrows() {
        const scrollable = this.rail.scrollWidth - this.rail.clientWidth > 4;
        [this.prev, this.next].forEach((button) => {
          if (button) button.hidden = !scrollable;
        });
      }

      onClick = (e) => {
        const toggle = e.target.closest('.cro-acc__toggle');
        if (toggle) {
          this.open(toggle);
          return;
        }
        const close = e.target.closest('.cro-acc__close');
        if (close) this.close(close.closest('.cro-acc__panel'), true);
      };

      onKeydown = (e) => {
        if (e.key !== 'Escape') return;
        const panel = e.target.closest('.cro-acc__panel');
        if (!panel) return;
        e.preventDefault();
        e.stopPropagation();
        this.close(panel, true);
      };

      onChange = (e) => {
        const input = e.target.closest('.cro-acc__panel input[name="id"]');
        if (!input) return;
        input
          .closest('.cro-acc__card')
          .querySelectorAll('.cro-acc__price')
          .forEach((price) => {
            price.textContent = input.dataset.price;
          });
      };

      open(toggle) {
        const panel = this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`);
        if (!panel) return;
        this.querySelectorAll('.cro-acc__panel[data-open]').forEach((other) => {
          if (other !== panel) this.close(other, false);
        });
        toggle.setAttribute('aria-expanded', 'true');
        this.cover(panel, true);
        panel.dataset.open = '';
        const options = panel.querySelectorAll('input[name="id"]:not(:disabled)');
        ([...options].find((input) => input.checked) ?? options[0])?.focus();
      }

      close(panel, restoreFocus) {
        if (!panel?.hasAttribute('data-open')) return;
        const toggle = this.querySelector(`[aria-controls="${panel.getAttribute('id')}"]`);
        delete panel.dataset.open;
        this.cover(panel, false);
        toggle?.setAttribute('aria-expanded', 'false');
        if (restoreFocus) toggle?.focus();
      }

      cover(panel, covered) {
        for (const child of panel.parentElement.children) child.inert = child === panel ? !covered : covered;
      }

      onSubmit = async (e) => {
        const form = e.target.closest('.cro-acc__form');
        if (!form) return;
        e.preventDefault();

        const id = new FormData(form).get('id');
        const button = form.querySelector('.cro-acc__atc');
        if (!id || button?.getAttribute('aria-disabled') === 'true') return;

        this.setStatus('');
        this.setLabel(button, 'add');
        this.setLoading(button, true);

        const sectionIds = [];
        document.querySelectorAll('cart-items-component').forEach((el) => {
          if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
        });

        try {
          const response = await fetch(`${Theme.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds.join(',') }),
          });

          if (!response.ok && response.status !== 422) throw new Error(`HTTP error! status: ${response.status}`);

          const data = await response.json();

          if (data.status) {
            const message = data.description || data.message || this.dataset.errorText;
            this.setStatus(message);
            this.setLabel(button, 'retry', 3000);
            window.dispatchEvent(new CartErrorEvent(this.id, message));
            (button ?? form).dispatchEvent(
              new CartAddEvent({}, String(id), {
                didError: true,
                source: 'cro-accessory-rail',
                itemCount: 1,
                productId: form.dataset.productId,
              })
            );
            return;
          }

          this.setStatus(this.dataset.addedText);
          this.setLabel(button, 'added', 2000);
          (button ?? form).dispatchEvent(
            new CartAddEvent(data, String(id), {
              source: 'cro-accessory-rail',
              itemCount: 1,
              productId: form.dataset.productId,
              sections: data.sections,
            })
          );
        } catch (error) {
          form.submit();
        } finally {
          this.setLoading(button, false);
        }
      };

      setLoading(button, isLoading) {
        if (!button) return;
        button.setAttribute('aria-disabled', String(isLoading));
        button.setAttribute('aria-busy', String(isLoading));
      }

      setLabel(button, label, duration) {
        if (!button) return;
        clearTimeout(button.labelTimer);
        button.querySelectorAll('[data-label]').forEach((text) => {
          text.classList.toggle('invisible', text.dataset.label !== label);
        });
        if (duration) button.labelTimer = setTimeout(() => this.setLabel(button, 'add'), duration);
      }

      setStatus(message) {
        const status = this.querySelector('.cro-acc__status');
        if (status) status.textContent = message;
      }

      slide(dir) {
        const max = this.rail.scrollWidth - this.rail.clientWidth;
        if (max <= 4) return;
        if (dir > 0 && this.rail.scrollLeft >= max - 4) {
          this.rail.scrollTo({ left: 0, behavior: 'smooth' });
        } else if (dir < 0 && this.rail.scrollLeft <= 4) {
          this.rail.scrollTo({ left: max, behavior: 'smooth' });
        } else {
          const step = this.rail.querySelector('.cro-acc__card')?.offsetWidth || this.rail.clientWidth * 0.5;
          this.rail.scrollBy({ left: dir * step, behavior: 'smooth' });
        }
      }
    }
  );
}
